import { useState, useMemo, useEffect, useRef } from "react";
import {
  Card,
  Spin,
  Empty,
  Button,
  Tag,
  Table,
  Collapse,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  Divider,
  message,
} from "antd";
import {
  ThunderboltOutlined,
  DownloadOutlined,
  LoadingOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Header from "../_shared/components/Header";
import {
  useTriggerHeadroomMutation,
  useCancelHeadroomMutation,
  useGetHeadroomStatusQuery,
  useGetPhantomTemplatesQuery,
  useCreatePhantomTemplateMutation,
  useUpdatePhantomTemplateMutation,
  useDeletePhantomTemplateMutation,
  useGetTasksQuery,
} from "../../../redux/api/scheduling";

// ---- Headroom Analysis Panel ----

function HeadroomPanel() {
  const [triggerHeadroom] = useTriggerHeadroomMutation();
  const [cancelHeadroom] = useCancelHeadroomMutation();
  const [polling, setPolling] = useState(false);
  const { data: statusData, refetch: refetchStatus } = useGetHeadroomStatusQuery(undefined, {
    pollingInterval: polling ? 3000 : 0,
    refetchOnMountOrArgChange: true,
  });
  const prevStatus = useRef<string | undefined>();

  useEffect(() => {
    if (!statusData) return;
    const s = statusData.status;
    if (s === "running" && !polling) {
      setPolling(true);
    }
    if (prevStatus.current === "running" && s !== "running") {
      setPolling(false);
      if (s === "complete") message.success("Headroom calculation complete");
      if (s === "error") message.error("Headroom calculation failed");
    }
    prevStatus.current = s;
  }, [statusData, polling]);

  const handleTrigger = async () => {
    try {
      await triggerHeadroom({}).unwrap();
      setPolling(true);
      message.info("Headroom calculation started");
    } catch (err: any) {
      if (err?.status === 409) {
        message.warning("Calculation already running");
        setPolling(true);
      } else {
        message.error("Failed to start headroom calculation");
      }
    }
  };

  const isRunning = statusData?.status === "running";
  const isComplete = statusData?.status === "complete";
  const isError = statusData?.status === "error";

  const resultMatrix = statusData?.matrix || {};
  const auditReport = statusData?.audit_report || [];

  const templateNames = Object.keys(resultMatrix);
  const allLeadTimes = useMemo(() => {
    const codes = new Set<string>();
    for (const counts of Object.values(resultMatrix)) {
      for (const code of Object.keys(counts as Record<string, number>)) {
        codes.add(code);
      }
    }
    return ["EX1", "EX2", "EX3", "EX4", "STD"].filter((c) => codes.has(c));
  }, [resultMatrix]);

  const matrixColumns: any[] = [
    {
      title: "Template",
      dataIndex: "template",
      key: "template",
      fixed: "left" as const,
    },
    ...allLeadTimes.map((lt) => ({
      title: lt,
      dataIndex: lt,
      key: lt,
      width: 100,
      render: (v: number) => {
        const color = v === 0 ? "red" : v >= 5 ? "green" : "orange";
        return <Tag color={color}>{v}</Tag>;
      },
    })),
  ];

  const matrixData = templateNames.map((name) => {
    const row: any = { key: name, template: name };
    const counts = resultMatrix[name] as Record<string, number>;
    for (const lt of allLeadTimes) {
      row[lt] = counts[lt] ?? 0;
    }
    return row;
  });

  const downloadAudit = () => {
    const lines = auditReport.map(
      (e: any) => `[${e.elapsed_s}s] ${e.timestamp}  ${e.message}`
    );
    const text = lines.join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `headroom-audit-${dayjs().format("YYYY-MM-DD-HHmmss")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      title="Headroom Analysis"
      extra={
        <Space>
          <Button
            type="primary"
            icon={isRunning ? <LoadingOutlined /> : <ThunderboltOutlined />}
            onClick={handleTrigger}
            loading={isRunning}
          >
            {isRunning ? "Running..." : "Run Headroom"}
          </Button>
          {isRunning && (
            <Button
              danger
              onClick={async () => {
                try {
                  await cancelHeadroom({}).unwrap();
                  setPolling(false);
                  refetchStatus();
                  message.info("Headroom cancelled");
                } catch (err: any) {
                  message.error(err?.data?.detail || "Failed to cancel");
                }
              }}
            >
              Cancel
            </Button>
          )}
        </Space>
      }
    >
      <p className="text-sm text-gray-500 mb-4">
        Tests how many additional phantom jobs the schedule can absorb before
        real jobs start running late. Each template is tested round-robin across
        its configured lead-time cascade.
      </p>

      {isRunning && (
        <div className="py-4">
          <div className="flex items-center gap-2 mb-2">
            <Spin size="small" />
            <span className="font-medium">
              Running headroom... Pass #{statusData?.scheduler_passes || 0}
            </span>
            <span className="text-gray-400 text-sm">
              ({(statusData?.audit_report || []).length} log entries)
            </span>
          </div>
          {(statusData?.audit_report || []).length > 0 && (
            <div className="font-mono text-xs max-h-64 overflow-auto bg-gray-50 p-3 rounded">
              {(statusData?.audit_report || []).map((entry: any, i: number) => (
                <div
                  key={i}
                  className={
                    entry.message.includes("FAILED")
                      ? "text-red-600"
                      : entry.message.includes("SUCCESS")
                        ? "text-green-600"
                        : entry.message.startsWith("=== Round")
                          ? "font-bold mt-1"
                          : ""
                  }
                >
                  <span className="text-gray-400">[{entry.elapsed_s}s]</span>{" "}
                  {entry.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isError && (
        <div className="text-red-500 mb-4">Error: {statusData?.error}</div>
      )}

      {isComplete && templateNames.length > 0 && (
        <>
          <h4 className="font-medium mb-2">Capacity Matrix (jobs that fit)</h4>
          <Table
            dataSource={matrixData}
            columns={matrixColumns}
            size="small"
            pagination={false}
            className="mb-4"
          />

          <Collapse
            size="small"
            items={[
              {
                key: "audit",
                label: `Audit Report (${auditReport.length} entries)`,
                extra: (
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadAudit();
                    }}
                  >
                    Download
                  </Button>
                ),
                children: (
                  <div className="font-mono text-xs whitespace-pre-wrap max-h-96 overflow-auto bg-gray-50 p-3 rounded">
                    {auditReport.map((entry: any, i: number) => (
                      <div
                        key={i}
                        className={
                          entry.message.includes("FAILED")
                            ? "text-red-600"
                            : entry.message.includes("SUCCESS")
                              ? "text-green-600"
                              : ""
                        }
                      >
                        <span className="text-gray-400">
                          [{entry.elapsed_s}s]
                        </span>{" "}
                        {entry.message}
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}

      {isComplete && templateNames.length === 0 && (
        <Empty description="No phantom templates configured. Add templates below to get started." />
      )}

      {!isRunning && !isComplete && !isError && (
        <Empty description="Click 'Run Headroom' to start the analysis" />
      )}
    </Card>
  );
}

// ---- Headroom Configuration Panel (embedded) ----

const LEAD_TIME_CODES = ["EX1", "EX2", "EX3", "EX4", "STD"] as const;
const JOB_MIX_OPTIONS = [
  { value: "", label: "None" },
  { value: "SS", label: "Start of Shift" },
  { value: "FS", label: "First Shift" },
  { value: "LO", label: "Overnight" },
  { value: "WE", label: "Weekend" },
];

interface IterationRule {
  lead_time_code: string;
  order: number;
}

function formatMinutes(mins: number): string {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function HeadroomConfigPanel() {
  const { data: templatesData, isLoading } = useGetPhantomTemplatesQuery({});
  const { data: tasksData } = useGetTasksQuery({});
  const [createTemplate] = useCreatePhantomTemplateMutation();
  const [updateTemplate] = useUpdatePhantomTemplateMutation();
  const [deleteTemplate] = useDeletePhantomTemplateMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [iterationRules, setIterationRules] = useState<IterationRule[]>([]);

  const templates = templatesData?.results || templatesData || [];
  const tasks = tasksData?.results || tasksData || [];

  const selectedTaskIds: number[] =
    Form.useWatch("required_task_ids", form) || [];

  const datetimeConditionTasks = useMemo(() => {
    if (!tasks.length || !selectedTaskIds.length) return [];
    return tasks.filter(
      (t: any) =>
        selectedTaskIds.includes(t.id) &&
        (t.conditions || []).some(
          (c: any) => c.condition_type === "datetime" && c.datetime_variable
        )
    );
  }, [tasks, selectedTaskIds]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setIterationRules([
      { lead_time_code: "EX1", order: 0 },
      { lead_time_code: "EX2", order: 1 },
      { lead_time_code: "EX3", order: 2 },
      { lead_time_code: "EX4", order: 3 },
      { lead_time_code: "STD", order: 4 },
    ]);
    form.setFieldsValue({
      lead_time_days: { EX1: 3, EX2: 5, EX3: 7, EX4: 10, STD: 20 },
      max_attempts: 10,
    });
    setModalOpen(true);
  };

  const openEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      job_mix: record.job_mix || "",
      required_task_ids: (record.required_tasks || []).map((id: number) => id),
      machine_time_minutes: record.machine_time_minutes,
      delivery_time_minutes: record.delivery_time_minutes,
      material_cutting_time_minutes: record.material_cutting_time_minutes,
      machine_setup_time_minutes: record.machine_setup_time_minutes,
      lead_time_days: record.lead_time_days || {},
      simulated_task_delays: record.simulated_task_delays || {},
      order: record.order,
      max_attempts: record.max_attempts ?? 10,
    });
    const rules = (record.iteration_rules || [])
      .slice()
      .sort((a: any, b: any) => a.order - b.order)
      .map((r: any, i: number) => ({
        lead_time_code: r.lead_time_code,
        order: i,
      }));
    setIterationRules(
      rules.length > 0
        ? rules
        : LEAD_TIME_CODES.map((code, i) => ({
            lead_time_code: code,
            order: i,
          }))
    );
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      iteration_rules: iterationRules.map((r, i) => ({
        lead_time_code: r.lead_time_code,
        order: i,
      })),
    };
    try {
      if (editingId) {
        await updateTemplate({ id: editingId, ...payload }).unwrap();
        message.success("Template updated");
      } else {
        await createTemplate(payload).unwrap();
        message.success("Template created");
      }
      setModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error saving template");
    }
  };

  const moveRule = (index: number, direction: "up" | "down") => {
    const updated = [...iterationRules];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= updated.length) return;
    [updated[index], updated[swapIdx]] = [updated[swapIdx], updated[index]];
    setIterationRules(updated.map((r, i) => ({ ...r, order: i })));
  };

  const removeRule = (index: number) => {
    setIterationRules((prev) =>
      prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, order: i }))
    );
  };

  const addRule = () => {
    const usedCodes = new Set(iterationRules.map((r) => r.lead_time_code));
    const nextCode = LEAD_TIME_CODES.find((c) => !usedCodes.has(c));
    if (!nextCode) {
      message.warning("All lead-time tiers are already configured");
      return;
    }
    setIterationRules((prev) => [
      ...prev,
      { lead_time_code: nextCode, order: prev.length },
    ]);
  };

  const updateRuleField = (
    index: number,
    field: keyof IterationRule,
    value: any
  ) => {
    setIterationRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  const configColumns: any[] = [
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: 70,
      sorter: (a: any, b: any) => a.order - b.order,
      defaultSortOrder: "ascend" as const,
    },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Job Mix",
      dataIndex: "job_mix",
      key: "job_mix",
      width: 120,
      render: (v: string) =>
        v ? (
          <Tag>{JOB_MIX_OPTIONS.find((o) => o.value === v)?.label || v}</Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Machine Time",
      dataIndex: "machine_time_minutes",
      key: "machine_time",
      width: 120,
      render: formatMinutes,
    },
    {
      title: "Lead-Time Days",
      key: "lead_time_days",
      width: 250,
      render: (_: any, r: any) => {
        const ltd = r.lead_time_days || {};
        return (
          <Space size={4} wrap>
            {LEAD_TIME_CODES.map((code) =>
              ltd[code] !== undefined ? (
                <Tag key={code} color="blue">
                  {code}: {ltd[code]}d
                </Tag>
              ) : null
            )}
          </Space>
        );
      },
    },
    {
      title: "Max Attempts",
      dataIndex: "max_attempts",
      key: "max_attempts",
      width: 110,
      render: (v: number) => v ?? "—",
    },
    {
      title: "Cascade Tiers",
      key: "rules",
      width: 200,
      render: (_: any, r: any) => {
        const rules = (r.iteration_rules || [])
          .slice()
          .sort((a: any, b: any) => a.order - b.order);
        return (
          <Space size={4} wrap>
            {rules.map((rule: any, i: number) => (
              <Tag key={i}>{rule.lead_time_code}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete this template?"
            onConfirm={() => deleteTemplate(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Phantom Job Templates"
      className="mt-4"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          Add Template
        </Button>
      }
    >
      {isLoading ? (
        <Spin size="large" className="mt-4 mx-auto block" />
      ) : (
        <Table
          dataSource={templates}
          columns={configColumns}
          rowKey="id"
          size="small"
          pagination={false}
        />
      )}

      <Modal
        title={editingId ? "Edit Phantom Template" : "Add Phantom Template"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="name"
              label="Template Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="e.g. Small Part – 2 hr machine" />
            </Form.Item>

            <Form.Item name="job_mix" label="Job Mix" initialValue="">
              <Select options={JOB_MIX_OPTIONS} />
            </Form.Item>

            <Form.Item name="order" label="Round-Robin Order" initialValue={0}>
              <InputNumber min={0} className="w-full" />
            </Form.Item>

            <Form.Item
              name="max_attempts"
              label="Max Attempts (total across all tiers)"
              initialValue={10}
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={200} className="w-full" />
            </Form.Item>

            <Form.Item name="required_task_ids" label="Required Tasks">
              <Select
                mode="multiple"
                placeholder="Select tasks"
                options={tasks.map((t: any) => ({
                  value: t.id,
                  label: `[${t.task_id}] ${t.name}`,
                }))}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>

          {datetimeConditionTasks.length > 0 && (
            <>
              <Divider orientation="left" plain>
                Simulated External Input Delays (non-holiday weekdays)
              </Divider>
              <p className="text-xs text-gray-500 mb-3">
                These tasks depend on external inputs. Set the number of
                non-holiday weekdays after the order received date that each
                input is simulated to arrive. Use 0 for immediate.
              </p>
              <div className="grid grid-cols-3 gap-x-4">
                {datetimeConditionTasks.map((t: any) => (
                  <Form.Item
                    key={t.id}
                    name={["simulated_task_delays", String(t.id)]}
                    label={t.name}
                    initialValue={0}
                  >
                    <InputNumber
                      min={0}
                      className="w-full"
                      addonAfter="days"
                    />
                  </Form.Item>
                ))}
              </div>
            </>
          )}

          <Divider orientation="left" plain>
            Duration Variables (minutes)
          </Divider>
          <div className="grid grid-cols-3 gap-x-4">
            <Form.Item
              name="machine_time_minutes"
              label="Machine Time"
              initialValue={0}
            >
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item
              name="delivery_time_minutes"
              label="Delivery Time"
              initialValue={0}
            >
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item
              name="material_cutting_time_minutes"
              label="Material Cutting"
              initialValue={0}
            >
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item
              name="machine_setup_time_minutes"
              label="Machine Setup"
              initialValue={0}
            >
              <InputNumber min={0} className="w-full" />
            </Form.Item>
          </div>

          <Divider orientation="left" plain>
            Lead-Time Days (non-holiday weekdays from today to due date)
          </Divider>
          <div className="grid grid-cols-5 gap-x-4">
            {LEAD_TIME_CODES.map((code) => (
              <Form.Item
                key={code}
                name={["lead_time_days", code]}
                label={code}
              >
                <InputNumber min={1} className="w-full" placeholder="days" />
              </Form.Item>
            ))}
          </div>

          <Divider orientation="left" plain>
            Cascade Iteration Rules
          </Divider>
          <p className="text-xs text-gray-500 mb-3">
            The headroom engine tries tiers in order. When a phantom job fails
            at the current tier, it cascades to the next. The total number of
            attempts is controlled by "Max Attempts" above. Use arrows to
            reorder.
          </p>
          <Card size="small">
            {iterationRules.map((rule, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 mb-2 last:mb-0"
              >
                <span className="text-xs text-gray-400 w-5 text-right">
                  {idx + 1}.
                </span>
                <Select
                  value={rule.lead_time_code}
                  onChange={(v) => updateRuleField(idx, "lead_time_code", v)}
                  options={LEAD_TIME_CODES.map((c) => ({
                    value: c,
                    label: c,
                  }))}
                  className="w-24"
                  size="small"
                />
                <Button
                  icon={<ArrowUpOutlined />}
                  size="small"
                  disabled={idx === 0}
                  onClick={() => moveRule(idx, "up")}
                />
                <Button
                  icon={<ArrowDownOutlined />}
                  size="small"
                  disabled={idx === iterationRules.length - 1}
                  onClick={() => moveRule(idx, "down")}
                />
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => removeRule(idx)}
                />
              </div>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={addRule}
              className="mt-2"
              disabled={iterationRules.length >= LEAD_TIME_CODES.length}
            >
              Add Rule
            </Button>
          </Card>
        </Form>
      </Modal>
    </Card>
  );
}

// ---- Main Headroom Page ----

export default function Headroom() {
  return (
    <div className="w-full p-4">
      <Header title="Headroom" />
      <HeadroomPanel />
      <HeadroomConfigPanel />
    </div>
  );
}
